'use client';
import React, { useState } from 'react';
import ProgressBar from '@/components/ProgressBar';
import StatusBadge from '@/components/StatusBadge';

const technicians = [
  { id: 'tp-001', name: 'Torres, J.', calls: 9, revPerHr: 284, revenue: '$2,556', rating: 4.9, upsell: 68, callQuality: 94, coaching: 'None', rank: 1 },
  { id: 'tp-002', name: 'Reyes, M.', calls: 8, revPerHr: 261, revenue: '$2,088', rating: 4.8, upsell: 54, callQuality: 88, coaching: 'None', rank: 2 },
  { id: 'tp-003', name: 'Park, D.', calls: 7, revPerHr: 248, revenue: '$1,736', rating: 4.7, upsell: 49, callQuality: 86, coaching: 'None', rank: 3 },
  { id: 'tp-004', name: 'Patel, A.', calls: 8, revPerHr: 219, revenue: '$1,752', rating: 4.6, upsell: 31, callQuality: 79, coaching: 'IAQ Upsell', rank: 4 },
  { id: 'tp-005', name: 'Chen, L.', calls: 6, revPerHr: 198, revenue: '$1,188', rating: 4.5, upsell: 28, callQuality: 74, coaching: 'Close Rate', rank: 5 },
  { id: 'tp-006', name: 'Williams, K.', calls: 9, revPerHr: 244, revenue: '$2,196', rating: 4.4, upsell: 44, callQuality: 82, coaching: 'None', rank: 6 },
  { id: 'tp-007', name: 'Santos, R.', calls: 7, revPerHr: 231, revenue: '$1,617', rating: 4.7, upsell: 41, callQuality: 85, coaching: 'None', rank: 7 },
  { id: 'tp-008', name: 'Kim, D.', calls: 8, revPerHr: 188, revenue: '$1,504', rating: 4.3, upsell: 22, callQuality: 69, coaching: 'Pricing Conf.', rank: 8 },
];

export default function TechnicianPerformanceTable() {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="bg-card rounded-lg card-shadow border border-border">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Technician Performance Intelligence</h3>
          <p className="text-xs text-muted-foreground mt-0.5">326 calls reviewed · 4 coaching alerts triggered</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">Top performer today</div>
          <div className="text-xs font-semibold text-success">Torres, J. — $284/hr</div>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs min-w-max">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">#</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Technician</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Calls</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Rev/Hr</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Revenue</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Rating</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Upsell%</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Call Quality</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Coaching</th>
            </tr>
          </thead>
          <tbody>
            {technicians?.map((t) => (
              <tr
                key={t?.id}
                onClick={() => setSelected(selected === t?.id ? null : t?.id)}
                className={`border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer ${selected === t?.id ? 'bg-secondary/5' : ''}`}
              >
                <td className="px-4 py-2.5">
                  <span className={`text-xs font-bold font-tabular ${t?.rank === 1 ? 'text-accent' : t?.rank === 2 ? 'text-muted-foreground' : 'text-muted-foreground/60'}`}>#{t?.rank}</span>
                </td>
                <td className="px-3 py-2.5 font-medium text-foreground whitespace-nowrap">{t?.name}</td>
                <td className="px-3 py-2.5 text-right font-tabular text-muted-foreground">{t?.calls}</td>
                <td className="px-3 py-2.5 text-right font-semibold font-tabular">
                  <span className={t?.revPerHr >= 260 ? 'text-success' : t?.revPerHr >= 220 ? 'text-warning' : 'text-danger'}>${t?.revPerHr}</span>
                </td>
                <td className="px-3 py-2.5 text-right font-semibold font-tabular text-foreground">{t?.revenue}</td>
                <td className="px-3 py-2.5 text-right font-tabular">
                  <span className={t?.rating >= 4.7 ? 'text-success font-semibold' : t?.rating >= 4.4 ? 'text-warning' : 'text-danger'}>{t?.rating}★</span>
                </td>
                <td className="px-3 py-2.5 w-28">
                  <ProgressBar value={t?.upsell} max={100} color={t?.upsell >= 50 ? 'success' : t?.upsell >= 35 ? 'warning' : 'danger'} size="sm" />
                  <span className="text-xs text-muted-foreground">{t?.upsell}%</span>
                </td>
                <td className="px-3 py-2.5 w-28">
                  <ProgressBar value={t?.callQuality} max={100} color={t?.callQuality >= 85 ? 'success' : t?.callQuality >= 75 ? 'warning' : 'danger'} size="sm" />
                  <span className="text-xs text-muted-foreground">{t?.callQuality}/100</span>
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge
                    variant={t?.coaching === 'None' ? 'complete' : 'warning'}
                    label={t?.coaching}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}